import { useTranslation } from "react-i18next"

import type { OptionItem } from "../schemas/column-schema"
import type { Column } from "../types"

import { Pencil } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"

import { OptionsBuilder } from "./options-builder"
import { useEditColumn } from "../hooks/use-edit-column"
import {
  DATA_TYPES,
  DEFAULT_OPTION_COLORS,
  getDataTypeDescription,
  getDataTypeLabel,
} from "../schemas/column-schema"

type EditColumnDialogProps = {
  children: React.ReactNode
  column: Column
}

export function EditColumnDialog({ children, column }: EditColumnDialogProps) {
  const { t } = useTranslation()
  const { editColumnForm, editColumn, editColumnMutation } = useEditColumn(column)

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              {t("pages.ai_parser.edit_column_dialog.title")}
            </div>
          </DialogTitle>
        </DialogHeader>
        <Form {...editColumnForm}>
          <form className="space-y-4" onSubmit={editColumnForm.handleSubmit(editColumn)}>
            <FormField
              control={editColumnForm.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("pages.ai_parser.edit_column_dialog.field_title")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t("pages.ai_parser.edit_column_dialog.title_placeholder")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={editColumnForm.control}
              name="dataType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("pages.ai_parser.edit_column_dialog.field_type")}</FormLabel>
                  <Select
                    defaultValue={field.value}
                    onValueChange={(value) => {
                      field.onChange(value)
                      if (value === "options") {
                        const currentOptions = editColumnForm.getValues("options") ?? []
                        if (currentOptions.length === 0) {
                          editColumnForm.setValue("options", [
                            {
                              id: crypto.randomUUID(),
                              label: "",
                              color: DEFAULT_OPTION_COLORS[0],
                            },
                          ])
                        }
                      }
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("pages.ai_parser.edit_column_dialog.select_data_type")}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {DATA_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {getDataTypeLabel(type, t)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>{getDataTypeDescription(field.value, t)}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {editColumnForm.watch("dataType") === "options" && (
              <div className="bg-muted/30 rounded-lg border p-4">
                <OptionsBuilder
                  options={editColumnForm.watch("options") ?? []}
                  optionTypeId={editColumnForm.watch("optionTypeId")}
                  onOptionsChange={(options: OptionItem[]) =>
                    editColumnForm.setValue("options", options)
                  }
                  onOptionTypeIdChange={(optionTypeId) =>
                    editColumnForm.setValue("optionTypeId", optionTypeId)
                  }
                />
              </div>
            )}
            <FormField
              control={editColumnForm.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("pages.ai_parser.edit_column_dialog.description")}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      className="min-h-[100px] resize-none"
                      placeholder={t("pages.ai_parser.edit_column_dialog.description_placeholder")}
                    />
                  </FormControl>
                  <FormDescription>
                    {t("pages.ai_parser.edit_column_dialog.description_help")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button disabled={editColumnMutation.isPending} type="button" variant="outline">
                {t("pages.ai_parser.edit_column_dialog.cancel")}
              </Button>
              <Button disabled={editColumnMutation.isPending} type="submit">
                {editColumnMutation.isPending ? (
                  <>
                    <Spinner />
                    {t("pages.ai_parser.edit_column_dialog.saving")}
                  </>
                ) : (
                  t("pages.ai_parser.edit_column_dialog.save_changes")
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
